import { useEffect, useRef, useState, useCallback } from 'react'
import GameRanking from '../components/GameRanking'
import { saveScore, loadGlobalRanking, loadPersonalRanking } from '../utils/firestoreService'
import UserHeader from '../components/UserHeader'
import GameStats from '../components/GameStats'
import RankingModal from '../components/RankingModal'
import './Snake.css'

const BOARD_WIDTH = 20
const BOARD_HEIGHT = 20
const BLOCK_SIZE = 25
const INITIAL_SPEED = 150
const SPEED_INCREMENT = 10

const DIRECTION = {
  UP: { x: 0, y: -1 },
  DOWN: { x: 0, y: 1 },
  LEFT: { x: -1, y: 0 },
  RIGHT: { x: 1, y: 0 }
}

export default function Snake({ user, onBackToMenu }) {
  const canvasRef = useRef()
  const [snake, setSnake] = useState([{ x: 10, y: 10 }])
  const [direction, setDirection] = useState(DIRECTION.RIGHT)
  const [nextDirection, setNextDirection] = useState(DIRECTION.RIGHT)
  const [food, setFood] = useState({ x: 15, y: 15 })
  const [score, setScore] = useState(0)
  const [gameOver, setGameOver] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [speed, setSpeed] = useState(INITIAL_SPEED)
  const [level, setLevel] = useState(1)
  const [levelUpNotification, setLevelUpNotification] = useState(null)
  const [globalRanking, setGlobalRanking] = useState([])
  const [personalRanking, setPersonalRanking] = useState([])
  const [showRanking, setShowRanking] = useState(false)
  const [savingScore, setSavingScore] = useState(false)
  const [scoreSaved, setScoreSaved] = useState(false)
  const gameLoopRef = useRef()

  const generateFood = useCallback(() => {
    let newFood
    do {
      newFood = {
        x: Math.floor(Math.random() * BOARD_WIDTH),
        y: Math.floor(Math.random() * BOARD_HEIGHT)
      }
    } while (snake.some(segment => segment.x === newFood.x && segment.y === newFood.y))
    return newFood
  }, [snake])

  const saveGameScore = useCallback(async (finalScore) => {
    if (!user || finalScore === 0) {
      console.log('Not saving score:', { user: !!user, score: finalScore })
      return
    }
    
    try {
      setSavingScore(true)
      await saveScore({
        userId: user.uid,
        userName: user.displayName || user.email,
        userPhoto: user.photoURL,
        game: 'snake',
        score: finalScore,
        level: Math.floor(finalScore / 50) + 1,
        lines: Math.floor(finalScore / 10)
      })
      setScoreSaved(true)
      setTimeout(() => setScoreSaved(false), 3000)
    } catch (error) {
      console.error('Erro ao salvar score:', error)
    } finally {
      setSavingScore(false)
    }
  }, [user])

  function checkCollision(head, snakeBody, currentLevel) {
    // Nível 15+: Paredes são fatais
    if (currentLevel >= 15) {
      if (head.x < 0 || head.x >= BOARD_WIDTH || head.y < 0 || head.y >= BOARD_HEIGHT) {
        return true
      }
    }
    // Colisão com o próprio corpo
    return snakeBody.some(segment => segment.x === head.x && segment.y === head.y)
  }

  function wrapPosition(head) {
    // Atravessa as paredes
    const wrapped = { ...head }
    if (wrapped.x < 0) wrapped.x = BOARD_WIDTH - 1
    if (wrapped.x >= BOARD_WIDTH) wrapped.x = 0
    if (wrapped.y < 0) wrapped.y = BOARD_HEIGHT - 1
    if (wrapped.y >= BOARD_HEIGHT) wrapped.y = 0
    return wrapped
  }

  const moveSnake = useCallback(() => {
    if (gameOver || isPaused) return
    
    const currentGenerateFood = generateFood

    setDirection(nextDirection)

    setSnake(prevSnake => {
      let head = { ...prevSnake[0] }
      head.x += nextDirection.x
      head.y += nextDirection.y

      // Calcula o nível atual
      const currentLevel = Math.floor(score / 50) + 1

      // Se nível < 15, aplica wrap (atravessa paredes)
      if (currentLevel < 15) {
        head = wrapPosition(head)
      }

      // Verifica colisão
      if (checkCollision(head, prevSnake, currentLevel)) {
        setGameOver(true)
        // Passa o score atual como parâmetro
        setScore(currentScore => {
          saveGameScore(currentScore)
          return currentScore
        })
        return prevSnake
      }

      const newSnake = [head, ...prevSnake]

      // Verifica se comeu a comida
      if (head.x === food.x && head.y === food.y) {
        setFood(currentGenerateFood())
        const newScore = score + 10
        const newLevel = Math.floor(newScore / 50) + 1
        
        setScore(newScore)
        setSpeed(prev => Math.max(50, prev - SPEED_INCREMENT))
        
        // Verifica mudança de nível
        if (newLevel !== currentLevel) {
          setLevel(newLevel)
          
          // Notificação especial no nível 15
          if (newLevel === 15) {
            setLevelUpNotification('⚠️ NÍVEL 15! Paredes agora são fatais!')
            setTimeout(() => setLevelUpNotification(null), 4000)
          } else if (newLevel < 15) {
            setLevelUpNotification(`🎮 Nível ${newLevel}! Você ainda pode atravessar paredes!`)
            setTimeout(() => setLevelUpNotification(null), 3000)
          } else {
            setLevelUpNotification(`🎮 Nível ${newLevel}!`)
            setTimeout(() => setLevelUpNotification(null), 2000)
          }
        }
      } else {
        newSnake.pop()
      }

      return newSnake
    })
  }, [nextDirection, food, gameOver, isPaused, score, generateFood, saveGameScore])

  const loadRankings = useCallback(async () => {
    try {
      const [global, personal] = await Promise.all([
        loadGlobalRanking('snake'),
        loadPersonalRanking(user?.uid, 'snake')
      ])
      setGlobalRanking(global)
      setPersonalRanking(personal)
    } catch (error) {
      console.error('Error loading rankings:', error)
    }
  }, [user])

  useEffect(() => {
    loadRankings()
  }, [loadRankings])

  // Previne scroll durante o jogo
  useEffect(() => {
    document.body.classList.add('game-active')
    
    return () => {
      document.body.classList.remove('game-active')
    }
  }, [])

  useEffect(() => {
    if (showRanking && !gameOver) {
      setIsPaused(true)
    }
  }, [showRanking, gameOver])

  useEffect(() => {
    const handleKeyPress = (e) => {
      e.preventDefault()
      
      // Tecla R para abrir/fechar ranking
      if (e.key === 'r' || e.key === 'R') {
        if (!gameOver && !showRanking) {
          setIsPaused(true)
        }
        setShowRanking(prev => !prev)
        return
      }

      if (gameOver || isPaused) return

      if (e.key === 'ArrowUp' && direction.y === 0) {
        setNextDirection(DIRECTION.UP)
      } else if (e.key === 'ArrowDown' && direction.y === 0) {
        setNextDirection(DIRECTION.DOWN)
      } else if (e.key === 'ArrowLeft' && direction.x === 0) {
        setNextDirection(DIRECTION.LEFT)
      } else if (e.key === 'ArrowRight' && direction.x === 0) {
        setNextDirection(DIRECTION.RIGHT)
      } else if (e.key === 'p' || e.key === 'P') {
        setIsPaused(prev => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [direction, gameOver, isPaused, showRanking])

  useEffect(() => {
    if (gameOver || isPaused) return

    gameLoopRef.current = setInterval(moveSnake, speed)
    return () => clearInterval(gameLoopRef.current)
  }, [moveSnake, gameOver, isPaused, speed])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Draw grid
    ctx.strokeStyle = '#1a1a2e22'
    for (let y = 0; y <= BOARD_HEIGHT; y++) {
      ctx.beginPath()
      ctx.moveTo(0, y * BLOCK_SIZE)
      ctx.lineTo(BOARD_WIDTH * BLOCK_SIZE, y * BLOCK_SIZE)
      ctx.stroke()
    }
    for (let x = 0; x <= BOARD_WIDTH; x++) {
      ctx.beginPath()
      ctx.moveTo(x * BLOCK_SIZE, 0)
      ctx.lineTo(x * BLOCK_SIZE, BOARD_HEIGHT * BLOCK_SIZE)
      ctx.stroke()
    }

    // Draw food
    ctx.fillStyle = '#f00000'
    ctx.shadowBlur = 15
    ctx.shadowColor = '#f00000'
    ctx.fillRect(
      food.x * BLOCK_SIZE + 2,
      food.y * BLOCK_SIZE + 2,
      BLOCK_SIZE - 4,
      BLOCK_SIZE - 4
    )
    ctx.shadowBlur = 0

    // Draw snake
    snake.forEach((segment, index) => {
      if (index === 0) {
        // Head
        ctx.fillStyle = '#00f000'
        ctx.shadowBlur = 15
        ctx.shadowColor = '#00f000'
      } else {
        // Body
        ctx.fillStyle = '#00d000'
        ctx.shadowBlur = 5
        ctx.shadowColor = '#00d000'
      }
      
      ctx.fillRect(
        segment.x * BLOCK_SIZE + 2,
        segment.y * BLOCK_SIZE + 2,
        BLOCK_SIZE - 4,
        BLOCK_SIZE - 4
      )
    })
    ctx.shadowBlur = 0
  }, [snake, food])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let touchStartX = 0
    let touchStartY = 0
    let touchStartTime = 0

    const handleTouchStart = (e) => {
      if (gameOver || isPaused) return
      const touch = e.touches[0]
      touchStartX = touch.clientX
      touchStartY = touch.clientY
      touchStartTime = Date.now()
    }

    const handleTouchEnd = (e) => {
      if (gameOver || isPaused) return
      const touch = e.changedTouches[0]
      const touchEndX = touch.clientX
      const touchEndY = touch.clientY
      const touchDuration = Date.now() - touchStartTime

      const deltaX = touchEndX - touchStartX
      const deltaY = touchEndY - touchStartY
      const absDeltaX = Math.abs(deltaX)
      const absDeltaY = Math.abs(deltaY)

      const minSwipeDistance = 30

      if (touchDuration < 200 && absDeltaX < minSwipeDistance && absDeltaY < minSwipeDistance) {
        return
      }

      if (absDeltaX > absDeltaY) {
        if (deltaX > 0 && direction.x === 0) {
          setNextDirection(DIRECTION.RIGHT)
        } else if (deltaX < 0 && direction.x === 0) {
          setNextDirection(DIRECTION.LEFT)
        }
      } else {
        if (deltaY > 0 && direction.y === 0) {
          setNextDirection(DIRECTION.DOWN)
        } else if (deltaY < 0 && direction.y === 0) {
          setNextDirection(DIRECTION.UP)
        }
      }
    }

    canvas.addEventListener('touchstart', handleTouchStart)
    canvas.addEventListener('touchend', handleTouchEnd)

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchend', handleTouchEnd)
    }
  }, [direction, gameOver, isPaused])

  function resetGame() {
    setSnake([{ x: 10, y: 10 }])
    setDirection(DIRECTION.RIGHT)
    setNextDirection(DIRECTION.RIGHT)
    setFood({ x: 15, y: 15 })
    setScore(0)
    setGameOver(false)
    setIsPaused(false)
    setSpeed(INITIAL_SPEED)
    setShowRanking(false)
    setSavingScore(false)
    setScoreSaved(false)
  }

  function handleTogglePause() {
    setIsPaused(!isPaused)
  }

  function handleToggleRanking() {
    if (!gameOver && !showRanking) {
      setIsPaused(true)
    }
    setShowRanking(!showRanking)
  }

  return (
    <div className="snake-container">
      <UserHeader user={user} gameName="SNAKE" />

      <div className="game-layout">
        <GameStats 
          score={score} 
          lines={Math.floor(score / 10)} 
          level={level} 
        />

        <div className="game-area">
          <canvas 
            ref={canvasRef} 
            width={BOARD_WIDTH * BLOCK_SIZE} 
            height={BOARD_HEIGHT * BLOCK_SIZE}
            className="game-canvas"
          />
          
          {/* Info Mobile Overlay */}
          <div className="mobile-info-overlay">
            <span>🏆 {score}</span>
            <span>📊 L{level}</span>
            <span>🍎 {Math.floor(score / 10)}</span>
          </div>
          
          {/* Level Up Notification */}
          {levelUpNotification && (
            <div className="level-notification">
              {levelUpNotification}
            </div>
          )}
          
          {/* Wall Warning for Level < 15 */}
          {!gameOver && level < 15 && (
            <div className="wall-mode-indicator">
              🔄 Modo: Atravessar Paredes
            </div>
          )}
          
          {/* Wall Warning for Level >= 15 */}
          {!gameOver && level >= 15 && (
            <div className="wall-mode-indicator danger">
              ⚠️ ATENÇÃO: Paredes são fatais!
            </div>
          )}
          
          {gameOver && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>GAME OVER</h2>
                <p className="final-score">Pontuação Final: <strong>{score}</strong></p>
                <p className="final-stats">
                  Nível {Math.floor(score / 50) + 1} • {Math.floor(score / 10)} comidas
                </p>
                
                {savingScore && (
                  <p className="saving-message">💾 Salvando pontuação...</p>
                )}
                
                {scoreSaved && (
                  <p className="saved-message">✅ Pontuação salva com sucesso!</p>
                )}
                
                <div className="game-over-buttons">
                  <button className="btn-primary" onClick={resetGame}>
                    🎮 Jogar Novamente
                  </button>
                  <button className="btn-secondary" onClick={() => setShowRanking(true)}>
                    🏆 Ver Rankings
                  </button>
                  {onBackToMenu && (
                    <button className="btn-secondary" onClick={onBackToMenu}>
                      ← Voltar ao Menu
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {isPaused && !gameOver && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>PAUSADO</h2>
                <p>Pressione P para continuar</p>
              </div>
            </div>
          )}
        </div>

        <div className="sidebar right-sidebar">
          <div className="controls-panel">
            <h3>CONTROLES</h3>
            <div className="control-item">↑ ↓ ← → Mover</div>
            <div className="control-item">P Pausar</div>
            <div className="control-item">R Ranking</div>
          </div>
          
          {!gameOver && (
            <button className="btn-secondary" onClick={handleTogglePause}>
              {isPaused ? 'Continuar' : 'Pausar'}
            </button>
          )}
          
          {onBackToMenu && (
            <button className="btn-back-menu" onClick={onBackToMenu}>
              ← Menu
            </button>
          )}
          
          <button className="btn-secondary" onClick={resetGame}>
            Reiniciar
          </button>
          
          <button className="btn-secondary" onClick={handleToggleRanking}>
            {showRanking ? 'Fechar Ranking' : 'Ver Ranking'}
          </button>
        </div>
      </div>

      <RankingModal
        show={showRanking}
        onClose={() => setShowRanking(false)}
        globalRanking={globalRanking}
        personalRanking={personalRanking}
        currentUserId={user?.uid}
        gameOver={gameOver}
      />


    </div>
  )
}
